import { HygieneScoreValueMapping, RiskValuekMapping } from 'consts/oralScoreConsts'

/**
 * Calculates the aggregated numeric value of the Oral Score based on the score values generated by the AI model.
 * Use `OralScoreValueMapping` to convert the value to `OralScores` enum if needed.
 *
 * The calculation is according to the description in the ticket: https://get-grin.atlassian.net/browse/GR-8717.
 *
 * @param {Object} args
 * @param {number} args.perio_risk - Perio risk value, between 1-3.
 * @param {number} args.dental_risk - Cavity risk value, between 1-3.
 * @param {number} args.hygiene_score - Hygiene score value, between 1-4.
 * @returns The calculated numeric OralScore value.
 */
export const calculateOralScore = ({ perio_risk, dental_risk, hygiene_score }) => {
  const totalRisks = Number(dental_risk > 1) + Number(perio_risk > 1)
  const highRisks = Number(dental_risk > 2) + Number(perio_risk > 2)
  const hasAnyHighRisk = highRisks > 1

  if (totalRisks <= 1 && !hasAnyHighRisk && hygiene_score <= 2) {
    return 1
  }

  if (highRisks <= 1 && hygiene_score <= 3) {
    return 2
  }

  return 3
}

const scoreTypes = {
  oral_hygiene_score: { prefix: 'hygiene', mapping: HygieneScoreValueMapping },
  dental_risk: { prefix: 'dental', mapping: RiskValuekMapping },
  periodontal_risk: { prefix: 'perio', mapping: RiskValuekMapping }
}

export const getPresentedScoreCategory = (oralScores = {}, scoreKey) => {
  if (oralScores?.errorCode) return undefined
  const { prefix, mapping } = scoreTypes[scoreKey]
  const presented = oralScores?.[`${prefix}_presented_category`]
  const original = oralScores?.[scoreKey]
  return mapping[presented] ? presented : mapping[original] ? original : undefined
}

export const getPresentedHygieneCategory = oralScores => getPresentedScoreCategory(oralScores, 'oral_hygiene_score')

export const getRawScoreValue = (oralScores, scoreKey) => oralScores?.[`${scoreTypes[scoreKey].prefix}_raw_value`]

// Compare against the model's category, which may include a dental-risk adjustment.
export const isScoreFrozen = (oralScores = {}, scoreKey) => {
  const { prefix, mapping } = scoreTypes[scoreKey]
  const original = oralScores?.[scoreKey]
  const presented = oralScores?.[`${prefix}_presented_category`]
  return (
    !oralScores?.errorCode &&
    oralScores?.[`${prefix}_presented_source`] === 'old' &&
    mapping[original] != null &&
    mapping[presented] != null &&
    Number(original) !== Number(presented)
  )
}

export const isHygieneScoreFrozen = oralScores => isScoreFrozen(oralScores, 'oral_hygiene_score')
